import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Button,
  IconButton,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Chip,
  Tooltip,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Divider
} from '@mui/material';
import {
  Highlight as HighlightIcon,
  Comment as CommentIcon,
  Note as NoteIcon,
  Delete as DeleteIcon,
  Save as SaveIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon
} from '@mui/icons-material';
import { fetchWithAuth } from '../../utils/auth';
import API_BASE_URL from '../../config/api';
import { useInteractiveTutorial } from '../../utils/InteractiveTutorialContext';
import { getButtonStyles, BUTTON_COLORS } from '../../config/buttonStyles';
import { useTheme } from '../../utils/ThemeContext';

const ArticleAnnotation = ({ article, onAnnotationsChange, isActive = true, sharedAnnotations = null, children }) => {
  // If shared annotations are provided, this is a read-only view
  const isReadOnly = sharedAnnotations && Array.isArray(sharedAnnotations);
  const { completeStep, isActive: isTutorialActive, getCurrentStep } = useInteractiveTutorial();
  const { isDarkMode } = useTheme();
  const [annotations, setAnnotations] = useState([]);
  const [selectedText, setSelectedText] = useState('');
  const [selectionRange, setSelectionRange] = useState(null);
  const [preservedSelection, setPreservedSelection] = useState(null);
  const [showAnnotationDialog, setShowAnnotationDialog] = useState(false);
  const [annotationType, setAnnotationType] = useState('highlight');
  const [annotationContent, setAnnotationContent] = useState('');
  const [annotationColor, setAnnotationColor] = useState('#ffff00');
  const [showAnnotations, setShowAnnotations] = useState(true);
  const [showCommentDialog, setShowCommentDialog] = useState(false);
  const [selectedComment, setSelectedComment] = useState(null);
  
  const articleRef = useRef(null);
  const tutorialStepsCompleted = useRef(new Set());
  const colors = [
    { name: 'Bright Yellow', value: '#ffff00' },
    { name: 'Bright Orange', value: '#ff8800' },
    { name: 'Bright Pink', value: '#ff69b4' },
    { name: 'Bright Green', value: '#00ff00' },
    { name: 'Bright Cyan', value: '#00ffff' },
    { name: 'Bright Purple', value: '#ff00ff' }
  ];

  // Tutorial: Auto-advance hooks removed to require explicit Continue at each step

  // Load annotations when article changes
  useEffect(() => {
    if (article?.id) {
      loadAnnotations();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [article?.id, sharedAnnotations]);


  // Handle text selection (disabled in read-only mode)
  useEffect(() => {
    // Don't add selection listeners if in read-only mode
    if (isReadOnly) {
      return;
    }

    const handleSelection = () => {
      const selection = window.getSelection();
      const selectedText = selection.toString().trim();
      
      if (selectedText && articleRef.current && articleRef.current.contains(selection.anchorNode)) {
        setSelectedText(selectedText);
        setSelectionRange({
          start: selection.anchorOffset,
          end: selection.focusOffset,
          text: selectedText
        });
      } else {
        setSelectedText('');
        setSelectionRange(null);
      }
    };

    const handleSelectionChange = () => {
      const selection = window.getSelection();
      const selectedText = selection.toString().trim();
      
      if (selectedText && articleRef.current && articleRef.current.contains(selection.anchorNode)) {
        setSelectedText(selectedText);
        setSelectionRange({
          start: selection.anchorOffset,
          end: selection.focusOffset,
          text: selectedText
        });
      } else {
        setSelectedText('');
        setSelectionRange(null);
      }
    };

    // Add multiple event listeners for better text selection detection
    document.addEventListener('mouseup', handleSelection);
    document.addEventListener('selectionchange', handleSelectionChange);
    document.addEventListener('keyup', handleSelection);
    
    return () => {
      document.removeEventListener('mouseup', handleSelection);
      document.removeEventListener('selectionchange', handleSelectionChange);
      document.removeEventListener('keyup', handleSelection);
    };
  }, [isReadOnly]);

  const loadAnnotations = async () => {
    try {
      
      // If shared annotations are provided, use them instead of loading from API
      if (sharedAnnotations && Array.isArray(sharedAnnotations)) {
        setAnnotations(sharedAnnotations);
        return;
      }
      
      // Try multiple ID formats to find annotations
      const possibleIds = [
        article.annotationId, // Prioritize annotationId if it exists
        article.id,
        // Try to reconstruct the annotation ID with different query patterns
        article.title ? `Unknown Query_${article.title}_${article.id || Date.now()}` : null,
        // Try with common query patterns that might be in the database
        article.title ? `(children) and (fever) and (paracetamol)_${article.title}_${article.id || Date.now()}` : null,
        article.title ? `(children) and (fever)_${article.title}_${article.id || Date.now()}` : null,
        article.title ? `(fever) and (paracetamol)_${article.title}_${article.id || Date.now()}` : null
      ].filter(Boolean);
      
      // Temporary debug log to see what IDs we're trying
      console.log('🔧 ArticleAnnotation trying IDs:', {
        articleId: article.id,
        annotationId: article.annotationId,
        possibleIds: possibleIds.map(id => id.substring(0, 80) + '...')
      });
      
      
      for (const id of possibleIds) {
        try {
          const response = await fetchWithAuth(`/api/annotations/load/${id}`);
          const data = await response.json();
          
          if (data.annotations && data.annotations.length > 0) {
            setAnnotations(data.annotations);
            return; // Found annotations, stop trying other IDs
          }
        } catch (error) {
        }
      }
      
      // If no annotations found by ID, try searching by title
      try {
        const searchResponse = await fetchWithAuth('/api/annotations/search-by-title', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            title: article.title
          })
        });
        const searchData = await searchResponse.json();
        
        if (searchData.annotations && searchData.annotations.length > 0) {
          // Validate that the annotations actually belong to this article
          const validAnnotations = searchData.annotations.filter(annotation => {
            // Check if the annotation text exists in the current article
            if (annotation.position && annotation.position.text) {
              return article.abstract && article.abstract.includes(annotation.position.text);
            }
            return true; // Keep annotations without position data
          });
          
          if (validAnnotations.length > 0) {
            setAnnotations(validAnnotations);
            return;
          }
        }
      } catch (error) {
      }
      
      setAnnotations([]);
    } catch (error) {
      console.error('Error loading annotations:', error);
    }
  };

  const saveAnnotation = async () => {
    try {
      // Use preserved selection if available, otherwise use current selection
      const positionData = preservedSelection?.range || selectionRange || {};
      
      const annotationData = {
        article_id: article.id,
        annotation_type: annotationType,
        content: annotationContent,
        position: positionData,
        color: annotationColor
      };

      const response = await fetchWithAuth('/api/annotations/article/save', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(annotationData)
      });

      if (response.ok) {
        await loadAnnotations();
        setShowAnnotationDialog(false);
        setAnnotationContent('');
        setSelectedText('');
        setSelectionRange(null);
        setPreservedSelection(null);
        // Tutorial: auto-advance on save for highlight/comment
        try {
          if (isTutorialActive && getCurrentStep) {
            const cs = getCurrentStep();
            if (cs?.id === 'save_highlight') {
              completeStep('save_highlight');
            } else if (cs?.id === 'save_comment') {
              completeStep('save_comment');
            }
          }
        } catch (_) {}
        if (onAnnotationsChange) {
          onAnnotationsChange();
        }
      }
    } catch (error) {
      console.error('Error saving annotation:', error);
    }
  };

  const deleteAnnotation = async (annotationId) => {
    try {
      const response = await fetchWithAuth(`/api/annotations/delete/${annotationId}`, {
        method: 'DELETE'
      });

      if (response.ok) {
        await loadAnnotations();
        if (onAnnotationsChange) {
          onAnnotationsChange();
        }
      }
    } catch (error) {
      console.error('Error deleting annotation:', error);
    }
  };

  const handleAnnotationClick = (type) => {
    if (selectedText) {
      // Preserve the current selection
      setPreservedSelection({
        text: selectedText,
        range: selectionRange
      });
      setAnnotationType(type);
      // Do not prefill content for highlight or comment; allow free-form note/comment
      if (type === 'highlight') {
        setAnnotationContent('');
        // Tutorial: auto-advance when highlight button clicked
        try {
          if (getCurrentStep && getCurrentStep()?.id === 'click_highlight_button') {
            completeStep('click_highlight_button');
          }
        } catch (_) {}
      } else if (type === 'comment') {
        setAnnotationContent('');
        // Tutorial: auto-advance when comment button clicked
        try {
          if (getCurrentStep && getCurrentStep()?.id === 'click_comment_button') {
            completeStep('click_comment_button');
          }
        } catch (_) {}
      } else {
        setAnnotationContent('');
      }
      setShowAnnotationDialog(true);
    } else {
      // For notes, allow creation without selection
      if (type === 'note') {
        setAnnotationType(type);
        setAnnotationContent('');
        setShowAnnotationDialog(true);
      }
    }
  };

  const renderHighlightedText = (text) => {
    if (!showAnnotations) return text;
    
    
    let highlightedText = text;
    
    // Process both highlights and comments
    const textAnnotations = annotations.filter(ann => 
      (ann.annotation_type === 'highlight' || ann.annotation_type === 'comment') && 
      ann.position && ann.position.text
    );
    
    
    textAnnotations
      .sort((a, b) => {
        const aStart = a.position?.start || 0;
        const bStart = b.position?.start || 0;
        return bStart - aStart;
      })
      .forEach(annotation => {
        const position = annotation.position || {};
        const { text: annotationText } = position;
        
        
        if (annotationText && annotationText.trim()) {
          let spanStyle = '';
          let title = '';
          
          if (annotation.annotation_type === 'highlight') {
            spanStyle = `background-color: ${annotation.color || '#ffff00'}; padding: 2px 4px; border-radius: 3px; cursor: pointer; color: #000;`;
            title = `Highlight: ${annotation.content || 'No note'} (Click to view/remove)`;
          } else if (annotation.annotation_type === 'comment') {
            spanStyle = `background-color: #ffd700; border-left: 3px solid #ffb300; padding: 2px 4px; border-radius: 3px; cursor: pointer; color: #000;`;
            title = `Comment: ${annotation.content || 'No comment'} (Click to view/remove)`;
          }
          
          const annotationSpan = `<span style="${spanStyle}" data-annotation-id="${annotation._id}" title="${title}">${annotationText}</span>`;
          
          // Try to find and replace the text
          highlightedText = highlightedText.replace(annotationText, annotationSpan);
          
        } else {
        }
      });
    
    return highlightedText;
  };


  // Toolbar content component to avoid duplication
  const renderToolbarContent = () => {
    // Don't show toolbar in read-only mode
    if (isReadOnly) {
      return null;
    }
    
    return (
    <>
      {selectedText && (
        <Box sx={{
          position: 'absolute',
          top: -12,
          left: 12,
          background: 'linear-gradient(135deg, var(--neon-cyan) 0%, #8e44ad 100%)',
          color: 'white',
          padding: '4px 12px',
          borderRadius: 'var(--radius-md)',
          fontSize: '0.8rem',
          fontWeight: 'bold',
          zIndex: 1001,
          boxShadow: '0 4px 12px rgba(108, 48, 130, 0.4)',
          border: '1px solid rgba(255, 255, 255, 0.2)',
          animation: 'pulse 2s infinite',
          '@keyframes pulse': {
            '0%': { transform: 'scale(1)' },
            '50%': { transform: 'scale(1.05)' },
            '100%': { transform: 'scale(1)' }
          }
        }}>
          ✨ Text Selected
        </Box>
      )}
      <Tooltip title={selectedText ? "Highlight selected text" : "Select text to highlight"}>
        <IconButton 
          data-tutorial="highlight-button"
          onClick={() => handleAnnotationClick('highlight')}
          disabled={!selectedText}
          sx={{ 
            color: selectedText ? 'black' : 'var(--text-muted)',
            background: selectedText ? 'linear-gradient(45deg, #ffff00 0%, #ff8800 20%, #ff69b4 40%, #00ff00 60%, #00ffff 80%, #ff00ff 100%)' : 'rgba(255, 255, 255, 0.1)',
            border: selectedText ? '2px solid rgba(255, 255, 255, 0.3)' : '1px solid var(--glass-border)',
            borderRadius: '4px',
            minWidth: '48px',
            minHeight: '48px',
            transition: 'opacity 0.2s ease',
            '&:hover': {
              opacity: 0.9,
            },
            '&:disabled': {
              backgroundColor: 'rgba(255, 255, 255, 0.05)',
              color: 'var(--text-muted)',
              border: '1px solid var(--glass-border)',
              transform: 'none',
              boxShadow: 'none'
            }
          }}
        >
          <HighlightIcon />
        </IconButton>
      </Tooltip>
      
      <Tooltip title={selectedText ? "Add comment to selected text" : "Select text to comment"}>
        <IconButton 
          data-tutorial="comment-button"
          onClick={() => handleAnnotationClick('comment')}
          disabled={!selectedText}
          sx={{ 
            color: selectedText ? 'black' : 'var(--text-muted)',
            background: selectedText ? 'linear-gradient(135deg, #ffd700 0%, #ffb300 100%)' : 'rgba(255, 255, 255, 0.1)',
            border: selectedText ? '2px solid rgba(255, 255, 255, 0.3)' : '1px solid var(--glass-border)',
            borderRadius: '4px',
            minWidth: '48px',
            minHeight: '48px',
            transition: 'opacity 0.2s ease',
            '&:hover': {
              opacity: 0.9,
            },
            '&:disabled': {
              backgroundColor: 'rgba(255, 255, 255, 0.05)',
              color: 'var(--text-muted)',
              border: '1px solid var(--glass-border)',
              transform: 'none',
              boxShadow: 'none'
            }
          }}
        >
          <CommentIcon />
        </IconButton>
      </Tooltip>
      
      <Tooltip title="Add general note (no text selection needed)">
        <IconButton 
          onClick={() => handleAnnotationClick('note')}
          sx={{ 
            color: 'white',
            background: 'linear-gradient(135deg, #468230 0%, #3a6b26 100%)',
            border: '2px solid rgba(255, 255, 255, 0.2)',
            borderRadius: '4px',
            minWidth: '48px',
            minHeight: '48px',
            transition: 'opacity 0.2s ease',
            '&:hover': {
              opacity: 0.9,
            },
            '& .MuiSvgIcon-root': {
              color: 'white'
            }
          }}
        >
          <NoteIcon />
        </IconButton>
      </Tooltip>
      
      <Divider orientation="vertical" flexItem />
      
      <Tooltip title="Toggle annotations visibility">
        <IconButton 
          onClick={() => setShowAnnotations(!showAnnotations)}
          sx={{ 
            color: showAnnotations ? 'white' : 'var(--text-muted)',
            background: showAnnotations ? 'linear-gradient(135deg, var(--neon-cyan) 0%, #8e44ad 100%)' : 'rgba(255, 255, 255, 0.1)',
            border: showAnnotations ? '2px solid rgba(255, 255, 255, 0.3)' : '1px solid var(--glass-border)',
            borderRadius: '4px',
            minWidth: '48px',
            minHeight: '48px',
            transition: 'opacity 0.2s ease',
            '&:hover': {
              opacity: 0.9,
            }
          }}
        >
          {showAnnotations ? <VisibilityIcon /> : <VisibilityOffIcon />}
        </IconButton>
      </Tooltip>
      
      {annotations.filter(ann => ann.annotation_type === 'highlight' || ann.annotation_type === 'comment').length > 0 && (
        <Chip 
          label={`${annotations.filter(ann => ann.annotation_type === 'highlight' || ann.annotation_type === 'comment').length} inline`}
          size="small"
          sx={{ 
            background: 'linear-gradient(135deg, #ffd700 0%, #ffb300 100%)',
            color: 'black',
            fontWeight: 'bold',
            border: '1px solid rgba(255, 255, 255, 0.2)',
            boxShadow: '0 2px 8px rgba(255, 215, 0, 0.3)',
            '&:hover': {
              opacity: 0.9,
            }
          }}
        />
      )}
      
      <Chip 
        label={`${annotations.filter(ann => ann.annotation_type === 'note').length} notes`}
        size="small"
        sx={{ 
          background: 'linear-gradient(135deg, #468230 0%, #3a6b26 100%)',
          color: 'white',
          ml: 'auto',
          fontWeight: 'bold',
          border: '1px solid rgba(255, 255, 255, 0.2)',
          boxShadow: '0 2px 8px rgba(70, 130, 48, 0.3)',
          '&:hover': {
            opacity: 0.9,
          }
        }}
      />
    </>
    );
  };

  return (
    <Box sx={{ position: 'relative', width: '100%' }}>
      {/* Read-only indicator for shared annotations */}
      {isReadOnly && (
        <Box sx={{
          position: 'absolute',
          top: -8,
          right: 8,
          background: 'linear-gradient(135deg, #ffd700 0%, #ffb300 100%)',
          color: 'black',
          padding: '4px 12px',
          borderRadius: 'var(--radius-md)',
          fontSize: '0.75rem',
          fontWeight: 'bold',
          zIndex: 1002,
          boxShadow: '0 2px 8px rgba(255, 215, 0, 0.4)',
          border: '1px solid rgba(255, 255, 255, 0.3)',
          display: 'flex',
          alignItems: 'center',
          gap: 0.5
        }}>
          👁️ Shared Annotations (Read-only)
        </Box>
      )}

      {/* Sticky Toolbar - First element, at the very top */}
      {isActive && !isReadOnly && (
        <Box 
          data-tutorial="annotation-toolbar"
          sx={{ 
            position: 'sticky',
            top: 10, // offset below tabs/nav so it doesn't get covered
            zIndex: 100,
            display: 'flex', 
            gap: 1, 
            p: 2,
            mb: 2,
            backgroundColor: 'var(--surface-2)',
            borderRadius: 'var(--radius-md)',
            border: '2px solid var(--neon-cyan)',
            boxShadow: '0 4px 12px rgba(108, 48, 130, 0.2)',
            backdropFilter: 'blur(10px)',
            '&:hover': {
              borderColor: '#8e44ad',
              boxShadow: '0 4px 16px rgba(108, 48, 130, 0.3)',
            }
          }}
        >
          {renderToolbarContent()}
        </Box>
      )}

      {/* Article Title, Metadata, etc. (from children) */}
      {children}
      
      {/* Abstract with Annotations */}
      <div className="modern-card" style={{ padding: '1.5rem', marginBottom: '1rem' }} data-tutorial="article-abstract">
        <Box 
          ref={articleRef}
          onClick={(e) => {
            // Check if clicked element has annotation data
            const target = e.target;
            if (target.hasAttribute('data-annotation-id')) {
              const annotationId = target.getAttribute('data-annotation-id');
              const annotation = annotations.find(ann => ann._id === annotationId);
              
              if (annotation) {
                if (annotation.annotation_type === 'comment') {
                  // Show comment content dialog
                  setSelectedComment(annotation);
                  setShowCommentDialog(true);
                } else {
                  // For highlights, show confirmation dialog
                  setSelectedComment(annotation);
                  setShowCommentDialog(true);
                }
              }
            }
          }}
          sx={{ 
            position: 'relative',
            scrollMarginTop: '88px', // ensure selected section scrolls below sticky areas
            '& .highlight': {
              backgroundColor: 'rgba(255, 255, 0, 0.3)',
              padding: '2px 4px',
              borderRadius: '3px',
              cursor: 'pointer'
            }
          }}
        >
          <Typography variant="h6" style={{ color: 'var(--text-primary)', marginBottom: '0.5rem' }}>
            Abstract
          </Typography>
          {article.abstract ? (
            <Typography 
              variant="body1" 
              style={{ color: 'var(--text-primary)', lineHeight: '1.6' }}
              dangerouslySetInnerHTML={{ 
                __html: renderHighlightedText(article.abstract) 
              }}
            />
          ) : (
            <Typography 
              variant="body2" 
              style={{ color: 'var(--text-muted)', fontStyle: 'italic' }}
            >
              No abstract available for this article.
            </Typography>
          )}
        </Box>
      </div>

      {/* Notes List (Only General Notes) */}
      {showAnnotations && annotations.filter(ann => ann.annotation_type === 'note').length > 0 && (
        <Box sx={{ mt: 3 }}>
          <Typography variant="h6" style={{ color: 'var(--text-primary)', marginBottom: '1rem' }}>
            {isReadOnly ? 'Shared Notes' : 'Your Notes'}
          </Typography>
          <List sx={{ backgroundColor: 'var(--surface-2)', borderRadius: 'var(--radius-md)' }}>
            {annotations
              .filter(annotation => annotation.annotation_type === 'note')
              .map((annotation, index) => (
              <ListItem key={annotation._id} sx={{ borderBottom: '1px solid var(--glass-border)' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mr: 2 }}>
                  <NoteIcon sx={{ color: '#468230' }} />
                </Box>
                <ListItemText
                  primary={annotation.content}
                  secondary={
                    <Typography variant="caption" sx={{ color: 'var(--text-muted)' }}>
                      General Note • {new Date(annotation.created_at).toLocaleDateString()}
                    </Typography>
                  }
                />
                {!isReadOnly && (
                  <ListItemSecondaryAction>
                    <IconButton 
                      onClick={() => deleteAnnotation(annotation._id)}
                      sx={{ color: 'var(--text-muted)' }}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </ListItemSecondaryAction>
                )}
              </ListItem>
            ))}
          </List>
        </Box>
      )}


      {/* Annotation Dialog */}
      <Dialog 
        open={showAnnotationDialog} 
        onClose={() => setShowAnnotationDialog(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            background: 'var(--surface-2)',
            border: '1px solid var(--glass-border)',
            borderRadius: 'var(--radius-lg)'
          }
        }}
      >
        <DialogTitle sx={{ color: 'var(--text-primary)' }}>
          {annotationType === 'highlight' ? 'Add Highlight' : annotationType === 'comment' ? 'Add Comment' : 'Add General Note'}
        </DialogTitle>
        <DialogContent>
          {(preservedSelection?.text || selectedText) && (
            <Box sx={{ mb: 2, p: 2, backgroundColor: 'var(--surface)', borderRadius: 'var(--radius-md)' }}>
              <Typography variant="subtitle2" sx={{ color: 'var(--text-muted)', mb: 1 }}>
                Selected Text:
              </Typography>
              <Typography variant="body2" sx={{ color: 'var(--text-primary)' }}>
                "{preservedSelection?.text || selectedText}"
              </Typography>
            </Box>
          )}
          
          {annotationType === 'highlight' && (
            <Box sx={{ mb: 2 }} data-tutorial="highlight-color-picker">
              <Typography variant="subtitle2" sx={{ color: 'var(--text-primary)', mb: 1 }}>
                Highlight Color:
              </Typography>
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                {colors.map((color) => (
                  <Box
                    key={color.value}
                    onClick={() => setAnnotationColor(color.value)}
                    sx={{
                      width: 30,
                      height: 30,
                      backgroundColor: color.value,
                      borderRadius: '50%',
                      cursor: 'pointer',
                      border: annotationColor === color.value ? '3px solid var(--neon-cyan)' : '1px solid var(--glass-border)',
                      '&:hover': { opacity: 0.9 }
                    }}
                  />
                ))}
              </Box>
            </Box>
          )}
          
          {(annotationType === 'comment' || annotationType === 'note') && (
            <TextField
              fullWidth
              multiline
              rows={3}
              value={annotationContent}
              onChange={(e) => setAnnotationContent(e.target.value)}
              placeholder={
                annotationType === 'comment' 
                  ? 'Add your comment about the selected text...' 
                  : 'Add a general note about this article...'
              }
              {...(annotationType === 'comment' ? { 'data-tutorial': 'comment-textarea' } : {})}
              sx={{
                '& .MuiOutlinedInput-root': {
                  color: 'var(--text-primary)',
                  '& fieldset': { borderColor: 'var(--glass-border)' },
                  '&:hover fieldset': { borderColor: '#6c3082' },
                  '&.Mui-focused fieldset': { borderColor: '#6c3082' }
                }
              }}
            />
          )}
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => setShowAnnotationDialog(false)}
            className="btn-modern"
            sx={getButtonStyles(false, isDarkMode, 'generic')}
          >
            Cancel
          </Button>
          <Button 
            onClick={saveAnnotation}
            disabled={annotationType === 'comment' ? !annotationContent.trim() : false}
            className="btn-modern"
            data-tutorial="highlight-save-button"
            {...(annotationType === 'comment' ? { 'data-tutorial-comment': 'comment-save-button' } : {})}
            startIcon={<SaveIcon />}
            sx={getButtonStyles(false, isDarkMode, 'generic')}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>

      {/* Comment/Annotation View Dialog */}
      <Dialog 
        open={showCommentDialog} 
        onClose={() => setShowCommentDialog(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            background: 'var(--surface-2)',
            border: '1px solid var(--glass-border)',
            borderRadius: 'var(--radius-lg)'
          }
        }}
      >
        <DialogTitle sx={{ color: 'var(--text-primary)' }}>
          {selectedComment?.annotation_type === 'highlight' ? 'Highlight Details' : 'Comment Details'}
        </DialogTitle>
        <DialogContent>
          {selectedComment?.annotation_type === 'comment' && selectedComment?.position?.text && (
            <Box sx={{ mb: 2, p: 2, backgroundColor: 'var(--surface)', borderRadius: 'var(--radius-md)' }}>
              <Typography variant="subtitle2" sx={{ color: 'var(--text-muted)', mb: 1 }}>
                Selected Text:
              </Typography>
              <Typography variant="body2" sx={{ color: 'var(--text-primary)' }}>
                "{selectedComment.position.text}"
              </Typography>
            </Box>
          )}
          
          {selectedComment?.annotation_type === 'comment' && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle2" sx={{ color: 'var(--text-primary)', mb: 1 }}>
                Comment:
              </Typography>
              <Typography variant="body1" sx={{ color: 'var(--text-primary)' }}>
                {selectedComment?.content || 'No content provided'}
              </Typography>
            </Box>
          )}
          
          {selectedComment?.annotation_type === 'highlight' && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="body1" sx={{ color: 'var(--text-primary)', textAlign: 'center' }}>
                Remove this highlight?
              </Typography>
            </Box>
          )}
          
          <Typography variant="caption" sx={{ color: 'var(--text-muted)' }}>
            Created: {selectedComment?.created_at ? new Date(selectedComment.created_at).toLocaleDateString() : 'Unknown'}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => setShowCommentDialog(false)}
            className="btn-modern"
            sx={getButtonStyles(false, isDarkMode, 'generic')}
          >
            Close
          </Button>
          <Button 
            onClick={() => {
              if (selectedComment) {
                deleteAnnotation(selectedComment._id);
                setShowCommentDialog(false);
              }
            }}
            className="btn-modern"
            startIcon={<DeleteIcon />}
            sx={getButtonStyles(false, isDarkMode, 'delete')}
          >
            Remove
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ArticleAnnotation;